'use client';
import { useState } from 'react';
import { MessageSquare, ChevronDown } from 'lucide-react';
import StarRating, { pluralReviews } from './StarRating';

interface Review {
  id: string;
  rating: number;
  comment?: string;
  createdAt: string;
  client?: {
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
}

interface ReviewsListProps {
  reviews: Review[];
}

export default function ReviewsList({ reviews }: ReviewsListProps) {
  const [showAll, setShowAll] = useState(false);

  if (!reviews.length) return null;

  const displayed = showAll ? reviews : reviews.slice(0, 3);

  return (
    <div id="reviews" className="bg-bg-surface rounded-2xl border border-border p-6 shadow-sm mb-6">
      <h2 className="text-lg font-bold text-text-primary mb-5 flex items-center gap-2">
        <MessageSquare size={18} className="text-brand" />
        Отзывы
        <span className="text-sm font-normal text-gray-400">({reviews.length})</span>
      </h2>
      <div className="space-y-4">
        {displayed.map((review) => (
          <div key={review.id} className="border-b border-border-subtle pb-4 last:border-0 last:pb-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                {review.client?.avatarUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={review.client.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-bg-muted flex items-center justify-center text-text-secondary font-semibold text-sm">
                    {review.client?.firstName?.[0]}{review.client?.lastName?.[0]}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-text-primary">
                    {review.client?.firstName} {review.client?.lastName}
                  </p>
                  <StarRating value={review.rating} />
                </div>
              </div>
              <span className="text-xs text-gray-400">
                {new Date(review.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            {review.comment && (
              <p className="text-sm text-text-secondary leading-relaxed ml-12">{review.comment}</p>
            )}
          </div>
        ))}
      </div>
      {reviews.length > 3 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 text-sm text-brand hover:underline flex items-center gap-1 mx-auto"
        >
          {showAll ? 'Скрыть' : `Показать все ${reviews.length} ${pluralReviews(reviews.length)}`}
          <ChevronDown size={14} className={`transition-transform ${showAll ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
}
